/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { AlertTriangle, ExternalLink, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const REASON_LABEL = {
  duplicate_pod: "POD duplicat",
  bad_cnp: "CNP greșit",
  customer_refusal: "Refuz client",
  doc_missing: "Doc lipsă",
  signature_invalid: "Semnătură invalidă",
  other: "Altul",
};

/**
 * Iter34c — Rework Queue Widget for Dashboard.
 *
 * Backend: GET /api/dashboard/rework-queue (RBAC-filtered).
 * Action: POST /api/sales/{id}/clear-rework (admin/BO/commercial owner).
 *
 * Props:
 *   user: current auth user (used for "own_agency" toggle label)
 */
export default function ReworkQueueWidget({ user, compactLimit }) {
  const [data, setData] = useState({ total: 0, items: [], shown: 0 });
  const [loading, setLoading] = useState(true);
  const [reasonFilter, setReasonFilter] = useState("__all__");
  const [ownAgencyOnly, setOwnAgencyOnly] = useState(false);
  // Iter52 P19 — when used on Dashboard (compactLimit set), default to compact view.
  // User can expand to full list inline. Detailed view stays on a dedicated page.
  const limit = compactLimit || 10;
  
  const canFilterAgency = user?.role === "manager" || user?.role === "agent";
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (reasonFilter && reasonFilter !== "__all__") params.set("problem_reason_code", reasonFilter);
      if (ownAgencyOnly && canFilterAgency) params.set("own_agency_only", "true");
      const r = await axios.get(`${API}/api/dashboard/rework-queue?${params.toString()}`);
      setData(r.data || { total: 0, items: [], shown: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea cozii de rework");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => { fetchData(); }, [reasonFilter, ownAgencyOnly]);
  // Iter53 P28 — „Rezolvă" eliminat din widget. Rezolvarea se face exclusiv din SaleDetail
  // (vezi banner-ul `rework-banner` din Overview / Quality flags panel).
  
  return (
    <Card data-testid="rework-queue-widget" className="border-amber-200">
      <CardHeader className="pb-2">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Coadă Rework
              <Badge className="ml-1 bg-amber-100 text-amber-900" data-testid="rework-queue-total">
                {data.total}
              </Badge>
            </CardTitle>
            <CardDescription>
              Vânzări cu probleme detectate care necesită refacere/intervenție.
            </CardDescription>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <Select value={reasonFilter} onValueChange={setReasonFilter}>
              <SelectTrigger className="h-8 w-full sm:w-44 text-xs" data-testid="rework-reason-filter">
                <SelectValue placeholder="Toate motivele" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" data-testid="rework-reason-all">Toate motivele</SelectItem>
                {Object.entries(REASON_LABEL).map(([val, lbl]) => (
                  <SelectItem key={val} value={val} data-testid={`rework-reason-${val}`}>
                    {lbl}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canFilterAgency && (
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={ownAgencyOnly}
                  onChange={(e) => setOwnAgencyOnly(e.target.checked)}
                  data-testid="rework-own-agency-toggle"
                />
                Doar agenția mea
              </label>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading && (
          <div className="py-6 text-center text-sm text-slate-500" data-testid="rework-loading">
            Se încarcă…
          </div>
        )}
        {!loading && data.items.length === 0 && (
          <div className="py-6 text-center text-sm italic text-slate-400" data-testid="rework-empty">
            Niciun rework în coadă. 🎉
          </div>
        )}
        {!loading && data.items.length > 0 && (
          <>
            {/* Iter53 P20 — Desktop table */}
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Client</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-slate-500">POD</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Agent</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Motiv</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Importat</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Acțiuni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((it) => {
                  const reasonLbl = REASON_LABEL[it.problem_reason_code] || it.problem_reason_code || "—";
                  return (
                    <tr
                      key={it.sale_id}
                      data-testid={`rework-row-${it.sale_id}`}
                      className="hover:bg-amber-50/60 cursor-pointer"
                      onClick={() => { window.location.href = `/sales/${it.sale_id}`; }}
                    >
                      <td className="px-2 py-2">
                        <Link to={`/sales/${it.sale_id}`} className="text-[#1e3a8a] hover:underline">
                          {it.client_name || <em className="text-slate-400">—</em>}
                        </Link>
                      </td>
                      <td className="px-2 py-2 font-mono text-xs text-slate-700">{it.pod || "—"}</td>
                      <td className="px-2 py-2 text-slate-700">{it.agent_name || "—"}</td>
                      <td className="px-2 py-2">
                        <Badge className="bg-orange-100 text-orange-900 text-[10px]">
                          {reasonLbl}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-500">
                        {it.created_at ? new Date(it.created_at).toLocaleDateString("ro-RO") : "—"}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {/* Iter53 P28 — „Deschide" în loc de „Rezolvă"; rezolvarea se face în SaleDetail. */}
                        <Link
                          to={`/sales/${it.sale_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 rounded border border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white h-7 px-2 text-xs font-medium transition-colors"
                          data-testid={`open-sale-${it.sale_id}`}
                        >
                          Deschide
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data.total > data.shown && (
              <div className="mt-3 text-center">
                <Link
                  to="/sales?requires_rework=true"
                  className="inline-flex items-center gap-1 text-xs text-[#1e3a8a] hover:underline"
                  data-testid="rework-see-all-link"
                >
                  Vezi toate ({data.total}) <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            )}
            </div>

            {/* Iter53 P20 — Mobile card list */}
            <div className="md:hidden flex flex-col gap-2" data-testid="rework-mobile-list">
              {data.items.map((it) => {
                const reasonLbl = REASON_LABEL[it.problem_reason_code] || it.problem_reason_code || "—";
                return (
                  <Link
                    key={it.sale_id}
                    to={`/sales/${it.sale_id}`}
                    className="block rounded-lg border border-amber-200 bg-amber-50/30 p-3 active:bg-amber-100/50"
                    data-testid={`rework-card-${it.sale_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="block text-sm font-medium text-[#1e3a8a] truncate">
                          {it.client_name || <em className="text-slate-400">— fără nume —</em>}
                        </p>
                        {it.pod && (
                          <p className="mt-0.5 font-mono text-[11px] text-slate-600">POD: {it.pod}</p>
                        )}
                        <p className="mt-0.5 text-xs text-slate-600">
                          {it.agent_name || "Agent neasociat"}
                        </p>
                      </div>
                      <Badge className="bg-orange-100 text-orange-900 text-[10px] shrink-0">
                        {reasonLbl}
                      </Badge>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[11px] text-slate-500">
                        {it.created_at ? new Date(it.created_at).toLocaleDateString("ro-RO") : "—"}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#1e3a8a]">
                        Deschide <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </Link>
                );
              })}
              {data.total > data.shown && (
                <div className="mt-2 text-center">
                  <Link
                    to="/sales?requires_rework=true"
                    className="inline-flex items-center gap-1 text-xs text-[#1e3a8a] hover:underline"
                    data-testid="rework-see-all-link-mobile"
                  >
                    Vezi toate ({data.total}) <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
